import { type PaginationState } from '@tanstack/react-table';
import { useCallback, useEffect, useState } from 'react';

import {
  getStoredPageSize,
  setStoredPageSize,
} from '~shared/helpers/page-size-preference';

export type FilterState = Record<string, string | boolean>;

// A valid `pageSize` URL param wins; otherwise fall back to the stored
// portal-wide preference before the caller's default.
const getInitialPageSize = (defaultPageSize: number): number => {
  if (typeof window === 'undefined') {
    return getStoredPageSize(defaultPageSize);
  }
  const urlPageSize = parseInt(
    new URLSearchParams(window.location.search).get('pageSize') ?? '',
    10,
  );
  if (!Number.isNaN(urlPageSize) && urlPageSize > 0) {
    return urlPageSize;
  }
  return getStoredPageSize(defaultPageSize);
};

export function useFilterPersistence<T extends FilterState>(
  initialFilters: T,
  defaultPageSize = 10,
) {
  // Get URL search params
  const getSearchParam = (param: string, defaultValue = 'All'): string => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const value = urlParams.get(param);
      return value ?? defaultValue;
    }
    return defaultValue;
  };

  // Helper to update URL search params
  const updateUrlParams = (params: Record<string, string | undefined>) => {
    const url = new URL(window.location.href);

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === 'All') {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    });

    window.history.replaceState({}, '', url.toString());
  };

  // Initialize filters from URL params
  const initializeFiltersFromUrl = useCallback((): T => {
    return Object.fromEntries(
      Object.entries(initialFilters).map(([key, defaultValue]) => {
        if (key === 'query') {
          return [key, getSearchParam(key, '')];
        }
        // Handle boolean values
        if (typeof defaultValue === 'boolean') {
          const urlValue = getSearchParam(key, String(defaultValue));
          return [key, urlValue === 'true'];
        }
        return [key, getSearchParam(key, defaultValue as string)];
      }),
    ) as T;
  }, [initialFilters]);

  // Initialize state from URL params
  const [filters, setFilters] = useState<T>(initializeFiltersFromUrl());

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: parseInt(getSearchParam('page', '0'), 10),
    pageSize: getInitialPageSize(defaultPageSize),
  });

  // Update a single filter
  const updateFilter = (key: keyof T, value: string | boolean) => {
    setFilters((prev) => ({ ...prev, [key]: value }) as T);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));

    // Convert boolean to string for URL params
    const urlValue = typeof value === 'boolean' ? String(value) : value;
    // For boolean false values, treat them as default and remove from URL
    const shouldIncludeInUrl = typeof value === 'boolean' 
      ? value === true 
      : urlValue !== 'All';
    
    updateUrlParams({
      [key.toString()]: shouldIncludeInUrl ? urlValue : undefined,
      page: '0',
    });
  };

  // Handle pagination changes
  const updatePagination = (
    newPagination:
      | PaginationState
      | ((prevState: PaginationState) => PaginationState),
  ) => {
    const updatedPagination =
      typeof newPagination === 'function'
        ? newPagination(pagination)
        : newPagination;

    setPagination(updatedPagination);
    updateUrlParams({
      page: updatedPagination.pageIndex.toString(),
      pageSize: updatedPagination.pageSize.toString(),
    });

    // Only persist explicit page-size changes — a pageSize carried in by the
    // URL (which always wins over the preference) shouldn't overwrite it.
    if (updatedPagination.pageSize !== pagination.pageSize) {
      setStoredPageSize(updatedPagination.pageSize);
    }
  };

  // Handle navigation to previous page
  const goToPreviousPage = () => {
    const newPageIndex = pagination.pageIndex - 1;
    setPagination({
      ...pagination,
      pageIndex: newPageIndex,
    });
    updateUrlParams({
      page: newPageIndex.toString(),
    });
  };

  // Handle navigation to next page
  const goToNextPage = () => {
    const newPageIndex = pagination.pageIndex + 1;
    setPagination({
      ...pagination,
      pageIndex: newPageIndex,
    });
    updateUrlParams({
      page: newPageIndex.toString(),
    });
  };

  // Handle popstate events (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      // Update filters from URL
      setFilters(initializeFiltersFromUrl());

      // Update pagination from URL
      setPagination({
        pageIndex: parseInt(getSearchParam('page', '0'), 10),
        pageSize: getInitialPageSize(defaultPageSize),
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [defaultPageSize, initializeFiltersFromUrl]);

  return {
    filters,
    pagination,
    updateFilter,
    updatePagination,
    goToPreviousPage,
    goToNextPage,
  };
}
